/**
 * 授權套裝／數量配額的共用顯示邏輯。
 *
 * ★★ 批93 多產品化：**這裡不再有 `QUOTA_LABELS`／`QUOTA_KEYS`／`PLAN_LABELS`**。
 *   有哪些配額鍵、叫什麼中文名、鍵缺席時產品端怎麼解讀（`absent_default`）、用量從心跳的
 *   哪個欄位讀（`usage_source`），全部由**該產品的憑證設計**（`ProductSpec`）提供。
 *   套裝中文名同理查 `spec.plans`。
 *
 * ★★ 批114 產品中立化：清單窄欄的「席次」欄改成**全部配額的精簡摘要**（`quotaSummary`），
 *   稽核／通知／確認框的文字改用 `quotaSummaryText` 組字。**本檔一個產品字面都不准有**
 *   （沒有 `seats`／`max_product_lines`／`partner_seats`、沒有「席次」「座席」），
 *   一切由憑證設計的 `spec.quotas` 驅動——看門狗 `lib/productVocabulary.spec.ts` 會掃。
 *
 * 所有函式都吃 `spec | null`：spec 還沒載回來時退化成「用原始英文 key 當標籤、缺席一律
 * 視為不限」——不猜、不擲錯。
 *
 * 與客戶 portal 的 `portal/src/lib/quotas.ts` **同構**（兩專案不共用套件，兩份要一起改）。
 */
import type { ProductSpec, QuotaSpec } from '@/api/ls'
import { planByKey } from '@/lib/products'

/** 授權套裝 → 中文名（`product_plans.key`；null/未知＝客製）。 */
export function planLabel(
  spec: ProductSpec | null | undefined,
  plan: string | null | undefined,
): string {
  if (!plan) return '客製'
  // ★ 批121：對不上任何套裝列的值一律印「客製」，**不要原樣印出鍵**。
  //   1.9.0 之前的 CS 站台把〈期間〉（`annual`／`monthly`）送進了申請的套裝欄，
  //   於是有一批憑證的 `plan` 是那種髒值；舊寫法 `?? plan` 會讓它以「annual」之姿
  //   出現在授權詳情與摘要行裡（主人 2026-09-21 回報「沒翻譯」）。
  //   原值仍看得到——清單與詳情都另有原始 JSON 可查，這裡只管人看的那一格。
  return planByKey(spec, plan)?.label ?? '客製'
}

export type Quotas = Record<string, number | null> | null | undefined

/** 憑證設計缺席時，用原始 key 當標籤湊出一列（語意一律「不限、無專屬用量欄」）。 */
function rawQuotaSpec(key: string): QuotaSpec {
  return {
    key,
    label: key,
    absent_default: null,
    usage_source: null,
    hint: null,
    deprecated: false,
  }
}

/**
 * 這個產品的配額欄定義（順序＝憑證設計的宣告順序）。
 *
 * spec 缺席時退回**資料自己帶的鍵**（`fallbackKeys`），標籤就是原字串——這樣至少不會
 * 整區消失，operator 還看得到數字（見 `views/DeploymentsView.vue::specFor` 的註解）。
 */
export function quotaDefs(
  spec: ProductSpec | null | undefined,
  fallbackKeys: string[] = [],
): QuotaSpec[] {
  if (spec?.quotas?.length) return spec.quotas
  return fallbackKeys.map(rawQuotaSpec)
}

function quotaOf(spec: ProductSpec | null | undefined, key: string): QuotaSpec | null {
  return (spec?.quotas ?? []).find((q) => q.key === key) ?? null
}

/** 配額鍵 → 中文名（spec 未載入／未知鍵一律回原始 key，不猜、不擲錯）。 */
export function quotaLabel(spec: ProductSpec | null | undefined, key: string): string {
  return quotaOf(spec, key)?.label ?? key
}

/**
 * 取某項上限：值 null 或整包 null → null（不限）；
 * **鍵缺席**→ 依憑證設計的 `absent_default`。spec 未知的鍵一律當 null。
 */
export function quotaLimit(
  spec: ProductSpec | null | undefined,
  quotas: Quotas,
  key: string,
): number | null {
  const v = quotas?.[key]
  if (v === undefined) return quotaOf(spec, key)?.absent_default ?? null
  return v
}

// --- 批114：憑證設計上的三個旗標（缺鍵預設 false／true／false）-----------------
//
// ★ 三支都寫成「缺鍵＝後端 schema 的預設值」，**不要在呼叫端各自 `?? false`**：
//   舊的憑證設計（migration 補旗標之前簽的產品、匯入的第三方 JSON）根本沒有這三個鍵，
//   每個呼叫端自己判一次，遲早有人把「缺鍵」判成相反的意思。

/** 簽發時這個鍵必須有值（缺鍵＝422）。憑證設計沒宣告＝不必填。 */
export function quotaRequired(q: QuotaSpec | null | undefined): boolean {
  return q?.required === true
}

/** 這個鍵可不可以是 null（無上限）。憑證設計沒宣告＝可以。 */
export function quotaAllowsUnlimited(q: QuotaSpec | null | undefined): boolean {
  return q?.allow_unlimited !== false
}

/** 客戶在自助 portal 的申請單裡可不可以填這個鍵。憑證設計沒宣告＝不可以。 */
export function quotaCustomerRequestable(q: QuotaSpec | null | undefined): boolean {
  return q?.customer_requestable === true
}

// --- 批151（裁示 98）：子證合併方式 `addon_merge` ---------------------------------
//
// 主證與生效子證的同名配額**怎麼合併**由每個配額鍵自己宣告。值域與順序照後端
// `services/products.py::ADDON_MERGE_MODES`；缺鍵（或值域外）＝`max`＝批93-B 起的「取最大值」。
// ★ 後端投影只在非預設值時帶這個鍵、寫入端也把預設值正規化掉 → 前端存檔時 `max` 一律不送。

export const ADDON_MERGE_MODES = ['max', 'min', 'sum', 'avg', 'latest'] as const
export type AddonMergeMode = (typeof ADDON_MERGE_MODES)[number]
export const ADDON_MERGE_DEFAULT: AddonMergeMode = 'max'

export const ADDON_MERGE_LABELS: Record<AddonMergeMode, string> = {
  max: '取最大值',
  min: '取最小值',
  sum: '加總',
  avg: '平均（四捨五入）',
  latest: '最新一張',
}

/** 這個配額鍵的合併方式（缺鍵或值域外＝`max`）。 */
export function quotaAddonMerge(q: QuotaSpec | null | undefined): AddonMergeMode {
  const v = q?.addon_merge
  return (ADDON_MERGE_MODES as readonly string[]).includes(v ?? '') ? (v as AddonMergeMode) : ADDON_MERGE_DEFAULT
}

/**
 * 回存用：把合併方式寫回一筆配額宣告——`max` 拿掉這個鍵（後端也會正規化掉，但不送才與
 * 投影一致），其他值原樣帶。**其餘鍵一律原樣保留**（含這一版前端不認得的新鍵）。
 */
export function withAddonMerge<T extends QuotaSpec>(q: T, mode: AddonMergeMode): T {
  const { addon_merge: _drop, ...rest } = q
  return (mode === ADDON_MERGE_DEFAULT ? rest : { ...rest, addon_merge: mode }) as T
}

// --- 加購數量級距 `addon_step`（11-01 裁示）---------------------------------------------
//
// 後端值域（`services/product_io.normalize_quota_specs`）：2～ADDON_STEP_MAX 的整數；缺鍵／null／1＝不檢查
// （存檔時拿掉這個鍵）。規則本體在後端 `products.check_addon_quota_steps`——前端只用它設定輸入框的
// step／min 並先擋非倍數，**不是**唯一真相。

/** 加購級距的上限（＝後端 `products.ADDON_STEP_MAX`）。 */
export const ADDON_STEP_MAX = 1_000_000_000

/** 這個配額鍵的加購級距；沒宣告（或值域外）＝`null`。 */
export function quotaAddonStep(q: QuotaSpec | null | undefined): number | null {
  const v = q?.addon_step
  return typeof v === 'number' && Number.isInteger(v) && v >= 2 && v <= ADDON_STEP_MAX ? v : null
}

/**
 * 表單文字 → 級距。空白＝`null`（不限制）；`1` 也是 `null`（每個正整數都是 1 的倍數）；
 * 2～上限的整數照收；其他（小數、負數、0、超過上限、非數字）→ `undefined`（表單擋住、不送出）。
 */
export function parseAddonStep(text: string | number | null | undefined): number | null | undefined {
  const t = String(text ?? '').trim()
  if (t === '') return null
  if (!/^\d{1,10}$/.test(t)) return undefined
  const n = Number(t)
  if (n === 1) return null
  if (n < 2 || n > ADDON_STEP_MAX) return undefined
  return n
}

/**
 * 回存用：把級距寫回一筆配額宣告——`null` 拿掉鍵（後端也會正規化掉，但不送才與投影一致），
 * **其餘鍵一律原樣保留**（同 `withAddonMerge`）。
 */
export function withAddonStep<T extends QuotaSpec>(q: T, step: number | null): T {
  const { addon_step: _drop, ...rest } = q
  return (step == null ? rest : { ...rest, addon_step: step }) as T
}

// --- 批169（裁示 81）：用量告警門檻 `usage_alert_pcts` 的表單文字 ----------------------
//
// 後端值域（`services/product_io.normalize_quota_specs`）：1～100 的整數、不重複、最多 4 個，存檔時
// 遞增排序；空＝拿掉這個鍵（不告警）。表單上是一格「90, 100」這樣的文字。

/** 一個配額鍵最多幾個告警門檻（＝後端 `products.USAGE_ALERT_MAX_PCTS`）。 */
export const USAGE_ALERT_MAX_PCTS = 4

/** 門檻 → 表單文字（`[90, 100]` → `90, 100`；沒有＝空字串）。 */
export function fmtAlertPcts(v: number[] | null | undefined): string {
  return (v ?? []).join(', ')
}

/**
 * 表單文字 → 門檻。空白＝`[]`（不告警）；任何一段不是 1～100 的整數、重複、或超過上限 → `null`
 * （表單擋住、不送出）。回傳已遞增排序。
 */
export function parseAlertPcts(text: string): number[] | null {
  const parts = text
    .split(/[,，、\s]+/)
    .map((s) => s.trim())
    .filter(Boolean)
  if (!parts.length) return []
  const out: number[] = []
  for (const p of parts) {
    if (!/^\d{1,3}$/.test(p)) return null
    const n = Number(p)
    if (n < 1 || n > 100 || out.includes(n)) return null
    out.push(n)
  }
  if (out.length > USAGE_ALERT_MAX_PCTS) return null
  return out.sort((a, b) => a - b)
}

/** 精確用量指標鍵的格式（＝後端心跳指標鍵 `^[a-z][a-z0-9_]{0,39}$`）。空字串＝不用。 */
export function usageMetricValid(text: string): boolean {
  const t = text.trim()
  return t === '' || /^[a-z][a-z0-9_]{0,39}$/.test(t)
}

/**
 * 回存用：把兩個批169 旗標寫回一筆配額宣告——空的就拿掉鍵（後端也會正規化掉，但不送才與投影一致），
 * **其餘鍵一律原樣保留**（同 `withAddonMerge`）。
 */
export function withUsageAlert<T extends QuotaSpec>(q: T, pcts: number[], metric: string): T {
  const { usage_alert_pcts: _p, usage_metric: _m, ...rest } = q
  const out: QuotaSpec = { ...rest }
  if (pcts.length) out.usage_alert_pcts = pcts
  if (metric.trim()) out.usage_metric = metric.trim()
  return out as T
}

/**
 * 一筆部署（或任何帶上限與用量的列）**最高的配額使用率**（%）；全部算不出來（不限／從未回報／
 * 沒有上限可比）→ null。批169：部署頁的「依使用率排序」與那顆色點吃同一個數字。
 * 上限 0（缺鍵且 `absent_default` 是 0）：用了就是滿的（100），沒用＝0。
 */
export function worstUsagePct(
  spec: ProductSpec | null | undefined,
  quotas: Quotas,
  usage: UsageLike | null | undefined,
): number | null {
  let worst: number | null = null
  for (const r of buildQuotaRows(spec, quotas, usage)) {
    if (r.limit == null || r.used == null) continue
    const p = r.limit <= 0 ? (r.used > 0 ? 100 : 0) : (r.used / r.limit) * 100
    if (worst == null || p > worst) worst = p
  }
  return worst
}

export function fmtLimit(n: number | null): string {
  return n == null ? '不限' : n.toLocaleString()
}

/** 「已用 / 上限」；沒回報過（used null）→「— / 上限」。 */
export function fmtUsed(used: number | null | undefined, limit: number | null): string {
  return `${used == null ? '—' : used.toLocaleString()} / ${fmtLimit(limit)}`
}

/**
 * 心跳回報的使用量（admin licenses.usage／hosts／deployments／portal hosts 同形的子集）。
 *
 * ★★ 批114 去產品化：這裡**只宣告 `quotas_in_use` 一個欄位**。專屬用量欄
 *   （憑證設計的 `usage_source`，例如某產品的協作用量）是**任意鍵名**，由 `usedOf`
 *   以安全轉型讀取。
 * ★★ 為什麼不寫成 `{ quotas_in_use: …; [k: string]: unknown }`：TypeScript 只給
 *   **型別別名**隱含索引簽章，**interface 沒有**——`Deployment`／`Host`／`LicenseUsage`
 *   都是 interface，目標型別一旦帶索引簽章，這些呼叫端全部會編不過
 *   （`Index signature for type 'string' is missing`）。多宣告的欄位在傳「變數」時
 *   本來就允許（多餘屬性檢查只針對字面值），所以窄宣告是最相容的寫法。
 */
export interface UsageLike {
  quotas_in_use: Record<string, number> | null
  /**
   * 批169：站台指標快照（部署列才有）。憑證設計宣告了 `usage_metric` 的配額鍵，用量優先讀這裡的
   * 精確值（見 `usedOf`）；沒有就退回整數用量。選填——授權的 `usage` 子物件與 portal 都沒有它。
   */
  metrics?: Record<string, number> | null
}

/**
 * 把各種用量投影正規化成 `UsageLike`（批114）。
 *
 * 三種來源形狀：
 * - ① 授權上的子物件（`license.usage`）——傳整張證或傳 `usage` 本身都吃。
 * - ② 主機／部署列的扁平欄（`*_in_use` 數字欄＋`quotas_in_use`）。
 * - ③ `null`／`undefined`／非物件 → `{ quotas_in_use: null }`（＝從未回報，只顯示上限）。
 *
 * 做法是**複製所有「鍵名以 `_in_use` 結尾且值為數字」的欄**＋`quotas_in_use`：憑證設計的
 * `usage_source` 指到哪個欄是**資料**，前端不該認得任何一個具體欄名。
 */
export function usageFrom(src: unknown): UsageLike {
  if (!src || typeof src !== 'object') return { quotas_in_use: null }
  const o = src as Record<string, unknown>
  // ① 傳進來的是整張證（帶 `usage` 子物件）→ 以子物件為準。
  if (o.usage && typeof o.usage === 'object') return usageFrom(o.usage)
  const out: Record<string, unknown> = {}
  for (const [k, v] of Object.entries(o)) {
    if (typeof v === 'number' && k.endsWith('_in_use')) out[k] = v
  }
  const q = o.quotas_in_use
  out.quotas_in_use = q && typeof q === 'object' ? (q as Record<string, number>) : null
  // 批169：站台指標（部署列）一併帶過去——`usage_metric` 要讀它。
  const m = o.metrics
  if (m && typeof m === 'object') out.metrics = m as Record<string, number>
  return out as unknown as UsageLike
}

export interface QuotaRow {
  key: string
  label: string
  /** null＝不限。 */
  limit: number | null
  /** null＝從未回報。 */
  used: number | null
  /** 已達上限（只擋新建、既有資料照常使用 → 畫面紅字提示不阻斷）。 */
  over: boolean
  /** `120 / 500`、`— / 不限`；沒回報過只印上限 `500`。 */
  text: string
}

/**
 * 讀某一項的用量。`usage_source` 非空＝該項有**專屬心跳欄**（不在 `quotas_in_use` 裡）；
 * null＝走用量快照。
 *
 * 批169：宣告了 `usage_metric`（精確用量指標）且這份用量帶著站台指標時，**優先**讀那個指標
 * ——與後端告警（`services/alerts.quota_usage_of`）同一套優先序，部署頁的使用率與告警看同一個數字。
 */
export function usedOf(
  usage: UsageLike,
  q: { key: string; usage_source: string | null; usage_metric?: string },
): number | null {
  if (q.usage_metric) {
    const precise = usage.metrics?.[q.usage_metric]
    if (typeof precise === 'number' && Number.isFinite(precise)) return precise
  }
  if (q.usage_source) {
    const v = (usage as unknown as Record<string, unknown>)[q.usage_source]
    return typeof v === 'number' ? v : null
  }
  return usage.quotas_in_use?.[q.key] ?? null
}

/**
 * 每項配額一列（順序＝憑證設計的宣告順序）。`usage` 為 null＝從未回報 → 只印上限。
 *
 * spec 為 null（還沒載回來／拿不到產品）時退化成「以授權上的 `quotas` 鍵集合為準、
 * 標籤用原始英文 key」——顯示會醜，但不會漏列也不會亂猜語意。
 */
export function buildQuotaRows(
  spec: ProductSpec | null | undefined,
  quotas: Quotas,
  usage: UsageLike | null | undefined,
): QuotaRow[] {
  // spec 缺席時的鍵集合＝授權上的 quotas ∪ 心跳回報的 quotas_in_use（能列多少列多少；
  // 兩邊都空就真的沒有列可畫——那是「operator 讀不到憑證設計」的已知缺口）。
  const fallbackKeys = [
    ...new Set([...Object.keys(quotas ?? {}), ...Object.keys(usage?.quotas_in_use ?? {})]),
  ]
  return quotaDefs(spec, fallbackKeys).map((def) => {
    const limit = quotaLimit(spec, quotas, def.key)
    const used = usage ? usedOf(usage, def) : null
    const over = limit != null && used != null && used > 0 && used >= limit
    const text = used == null ? fmtLimit(limit) : fmtUsed(used, limit)
    return { key: def.key, label: def.label, limit, used, over, text }
  })
}

/**
 * 配額欄的顯示順序與中文名（套裝比較表這類「欄是配額鍵」的表格用）。
 *
 * `fallbackKeys`＝spec 缺席時要顯示哪些欄；不給就是不顯示。
 */
export function quotaColumns(
  spec: ProductSpec | null | undefined,
  fallbackKeys: string[] = [],
): QuotaSpec[] {
  return quotaDefs(spec, fallbackKeys)
}

// --- 批114：精簡摘要（清單窄欄／稽核與通知文字）-------------------------------

export interface QuotaSummaryItem {
  key: string
  label: string
  /** null＝不限。 */
  limit: number | null
  /** null＝從未回報（或呼叫端沒給用量）。 */
  used: number | null
  /** 已達上限。 */
  over: boolean
  /** 接近上限（已用／上限 ≥ 80%，且還沒滿）——只有 `limit > 0` 才算得出來。 */
  near: boolean
  /** 緊湊文字：`8/15`／`8/不限`／`15`／`不限`（窄欄不塞空白）。 */
  text: string
}

export interface QuotaSummaryResult {
  /** 要顯示的前 `limit` 項。 */
  items: QuotaSummaryItem[]
  /** 沒顯示的項數（`+N` 的 N）。 */
  rest: number
  /** 全部（排序後）——tooltip 列全部、測試對排序用。 */
  all: QuotaSummaryItem[]
  /** 有任何一項已達上限 → 呼叫端上警示色。 */
  warn: boolean
}

/** 摘要用的緊湊文字（窄欄不塞空白，`fmtUsed` 的空白會讓 1366 寬的表格換行）。 */
function compactText(used: number | null, limit: number | null): string {
  return used == null ? fmtLimit(limit) : `${used.toLocaleString()}/${fmtLimit(limit)}`
}

/**
 * 全部配額的精簡摘要（批114 裁示 ⑯⑳）。
 *
 * - 預設順序＝憑證設計的宣告順序；**有用量時**把「已達上限」排到最前、其次「接近上限」，
 *   其餘維持宣告順序（`Array.prototype.sort` 在 ES2019 起保證穩定）。
 * - `usage` 吃任何形狀（見 `usageFrom`）：授權的 `usage` 子物件、主機／部署列、或 null。
 * - **已停用（`deprecated`）的鍵不進摘要**，除非這張證真的帶了那個鍵——停用鍵通常是
 *   「不再賣、但舊證還有」，塞進只有兩格的摘要會把真正該看的鍵擠掉。
 */
export function quotaSummary(
  spec: ProductSpec | null | undefined,
  quotas: Quotas,
  usage?: unknown,
  opts?: { limit?: number; template?: boolean },
): QuotaSummaryResult {
  const max = opts?.limit ?? 2
  const rows = buildQuotaRows(spec, quotas, usage == null ? null : usageFrom(usage))
  const deprecated = new Set(
    (spec?.quotas ?? []).filter((q) => q.deprecated).map((q) => q.key),
  )
  const carried = new Set(Object.keys(quotas ?? {}))
  const all: QuotaSummaryItem[] = rows
    .filter((r) => !deprecated.has(r.key) || carried.has(r.key))
    .map((r) => {
      // 套裝**模板**缺鍵＝這個套裝不預設這一項、核發時由操作者填（「客製」）；
      // 不能像授權那樣套 `absent_default`——企業級模板沒有席次鍵，印成「座席 0」
      // 會讓人以為賣的是 0 席（批114 走查實抓）。
      if (opts?.template && !carried.has(r.key)) {
        return { key: r.key, label: r.label, limit: null, used: null, over: false, near: false, text: '客製' }
      }
      return {
        key: r.key,
        label: r.label,
        limit: r.limit,
        used: r.used,
        over: r.over,
        near:
          !r.over && r.limit != null && r.limit > 0 && r.used != null && r.used / r.limit >= 0.8,
        text: compactText(r.used, r.limit),
      }
    })
  // 穩定排序：over → near → 其餘（各組內維持宣告順序）。沒有用量時三組都落在 rank 2，
  // 等於完全不動順序。
  all.sort((a, b) => rank(a) - rank(b))
  const items = max >= 0 ? all.slice(0, max) : all.slice()
  return { items, rest: all.length - items.length, all, warn: all.some((r) => r.over) }
}

function rank(r: QuotaSummaryItem): number {
  return r.over ? 0 : r.near ? 1 : 2
}

/**
 * 摘要的純文字版（批114 裁示 ⑰）——確認框、稽核與通知文字用。
 *
 * 例：`資料保留 15・裝置數 3・+11`。沒有任何宣告鍵時回**空字串**（呼叫端就整段省略，
 * 不要印「配額：」後面空一片）。
 */
export function quotaSummaryText(
  spec: ProductSpec | null | undefined,
  quotas: Quotas,
  opts?: { limit?: number },
): string {
  const s = quotaSummary(spec, quotas, null, opts)
  if (!s.all.length) return ''
  const parts = s.items.map((r) => `${r.label} ${r.text}`)
  if (s.rest > 0) parts.push(`+${s.rest}`)
  return parts.join('・')
}
